/*
  CEBE theme — assets/cebe.js, part 1 of 4: helpers, header, menu and cart drawer.
  Load all parts in this order, each with defer: cebe-part-1.js, cebe-part-2.js, cebe-part-3.js, cebe-part-4.js.
  Made by website/review/sammel-2026-10-03/werkzeuge/zusammenfuehren.py from assets/cebe.js (MD5 46a65575485db7a4e2796e006f51aee3);
  every Shopify upload file must stay below 26 000 bytes. Between the markers stands the original code; the only change:
  a name that one part changes and another reads, or that comes from a later part, reads cebeShared.<name> (one private
  object, window.__cebeParts.main). See website/review/sammel-2026-10-03/TEILUNG.md.
*/
/*
  CEBE theme — progressive enhancement only. Every action also works without this file:
  the product form posts to /cart/add, the header cart link opens /cart.
  No framework, no tracking, no third-party code.
*/
(function () {
  'use strict';

  var cebeShared = (window.__cebeParts = window.__cebeParts || {}).main || (window.__cebeParts.main = {});
  cebeShared.done = 0; // a new run of part 1 starts the chain again

  /* ---- assets/cebe.js, lines 8–389 ---- */

  var config = window.cebe || { routes: {}, strings: {} };
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function $(selector, root) {
    return (root || document).querySelector(selector);
  }

  function $$(selector, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(selector));
  }

  /* Screen reader messages. Inside an open modal dialog the page live region is not in the
     accessibility tree, so the drawer has its own. */
  function announce(message) {
    var live = cebeShared.drawer && cebeShared.drawer.open ? $('[data-cebe-drawer-live]', cebeShared.drawer) : $('[data-cebe-live]');
    if (!live || !message) return;
    live.textContent = '';
    window.setTimeout(function () {
      live.textContent = message;
    }, 50);
  }

  /* Only a real cart error from Shopify (JSON 422 with a description, e.g. stock) is shown as it is. A 400 (bad request)
     is rejected too, but says the calm general sentence; the caller reloads the cart from the server. Anything else
     (network, 5xx, a proxy's HTML page) becomes the general sentence as well, never a browser exception text. */
  function readJson(response) {
    return response.json().then(
      function (data) {
        if (response.ok) return data;
        var rejected = response.status === 422 || response.status === 400;
        throw { rejected: rejected, cart: response.status === 422 && data && typeof data.description === 'string' && data.description };
      },
      function () {
        throw {};
      }
    );
  }

  function errorText(error) {
    return (error && typeof error.cart === 'string' && error.cart) || config.strings.error;
  }

  /* A cart request that hangs must not hold the cart queue, and with it "Check out" and "Add to cart", for ever: after
     `wait` ms it is stopped (the server may still carry it out). Rejects like readJson, or with { timeout: true }. */
  var CART_WAIT = 10000;
  function limitedJson(url, options, wait) {
    var controller = window.AbortController ? new window.AbortController() : null;
    if (controller) options.signal = controller.signal;
    return new Promise(function (resolve, reject) {
      var timer = window.setTimeout(function () {
        if (controller) controller.abort();
        reject({ timeout: true });
      }, wait);
      fetch(url, options)
        .then(readJson)
        .then(
          function (data) {
            window.clearTimeout(timer);
            resolve(data);
          },
          function (error) {
            window.clearTimeout(timer);
            reject(error);
          }
        );
    });
  }

  /* ---------- Header: hairline once the page scrolls ---------- */
  function initHeader() {
    var header = $('[data-cebe-header]');
    if (!header) return;
    var ticking = false;
    function update() {
      if (window.scrollY > 0) header.setAttribute('data-scrolled', '');
      else header.removeAttribute('data-scrolled');
      ticking = false;
    }
    window.addEventListener(
      'scroll',
      function () {
        if (!ticking) {
          window.requestAnimationFrame(update);
          ticking = true;
        }
      },
      { passive: true }
    );
    update();
  }

  /* ---------- Mobile menu: Esc closes, focus stays in burger + menu, page behind is inert ---------- */
  function initMenu() {
    var toggle = $('[data-cebe-menu-toggle]');
    var menu = $('[data-cebe-menu]');
    if (!toggle || !menu) return;
    var label = $('[data-cebe-menu-label]', toggle);

    function focusables() {
      // Document order: the burger sits before the menu.
      return [toggle].concat($$('a[href], button:not([disabled]), input, select, summary', menu).filter(function (node) { return node.getClientRects().length > 0; }));
    }

    function setBackgroundInert(inert) {
      $$('#MainContent, .cebe-footer-section').forEach(function (node) {
        node.inert = inert;
      });
    }

    function setOpen(open) {
      toggle.setAttribute('aria-expanded', String(open));
      if (label) label.textContent = open ? toggle.dataset.labelClose : toggle.dataset.labelOpen;
      menu.hidden = !open;
      document.documentElement.classList.toggle('cebe-menu-open', open);
      setBackgroundInert(open);
      if (open) {
        var first = $('a[href]', menu);
        if (first) first.focus();
      }
    }

    toggle.addEventListener('click', function () {
      setOpen(toggle.getAttribute('aria-expanded') !== 'true');
    });

    document.addEventListener('keydown', function (event) {
      if (menu.hidden) return;
      if (event.key === 'Escape') {
        setOpen(false);
        toggle.focus();
        return;
      }
      if (event.key === 'Tab') {
        var items = focusables();
        var first = items[0];
        var last = items[items.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    });

    window.matchMedia('(min-width: 1001px)').addEventListener('change', function (event) {
      if (event.matches && !menu.hidden) setOpen(false);
    });

    config.closeMenu = function () {
      if (!menu.hidden) setOpen(false);
    };
  }

  /* ---------- Cart: drawer and cart page (native dialog + Section Rendering API) ----------
     Only elements with data-cebe-part are replaced (count, body or lines, summary). Everything else stays in
     place: the close button keeps focus, "Check out" and the express block are never re-created. */
  var SECTION = 'cebe-cart-drawer';
  cebeShared.drawer = null;
  cebeShared.lastTrigger = null;
  cebeShared.closeTimer = 0;
  cebeShared.edgeTicking = false;

  function motionOk() {
    return !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function toggleAttr(node, name, on) {
    if (on) node.setAttribute(name, '');
    else node.removeAttribute(name);
  }

  function parse(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  function fill(template, token, value) {
    return template.replace(token, function () {
      return value;
    });
  }

  // The header number and label come from the hidden source in the drawer title. Returns the count.
  function updateCount(root) {
    var source = $('[data-cebe-cart-count-source]', root || document);
    if (!source) return null;
    var count = parseInt(source.textContent, 10) || 0;
    $$('[data-cebe-cart-count]').forEach(function (node) {
      node.textContent = String(count);
    });
    var link = $('[data-cebe-cart-open]');
    if (link && source.dataset.label) link.setAttribute('aria-label', source.dataset.label);
    return count;
  }

  function currentCount() {
    var source = cebeShared.drawer && $('[data-cebe-cart-count-source]', cebeShared.drawer);
    return source ? parseInt(source.textContent, 10) || 0 : null;
  }

  function showError(node, message) {
    if (!node) return;
    node.textContent = message || '';
    node.hidden = !message;
  }

  function lineByKey(scope, key) {
    return $$('[data-cebe-key]', scope).filter(function (node) {
      return node.dataset.cebeKey === key;
    })[0];
  }

  // Replace each part with its fresh twin, then put the old, already decoded line images back: nothing flashes.
  function swapParts(scope, fresh) {
    var images = {};
    $$('.cebe-line__media img', scope).forEach(function (img) {
      var src = img.getAttribute('src');
      (images[src] = images[src] || []).push(img);
    });
    $$('[data-cebe-part]', scope).forEach(function (node) {
      var next = $('[data-cebe-part="' + node.dataset.cebePart + '"]', fresh);
      if (next) node.replaceWith(next);
    });
    $$('.cebe-line__media img', scope).forEach(function (img) {
      var old = images[img.getAttribute('src')];
      if (old && old.length) img.replaceWith(old.shift());
    });
  }

  // Where focus was: a line control by line key (the same control gets it back after the swap), or elsewhere in scope.
  function focusDescriptor(scope) {
    var active = document.activeElement;
    if (!active || !scope.contains(active)) return null;
    var line = active.closest('[data-cebe-key]');
    if (!line) return { key: null };
    var control = 'input';
    if (active.matches('[data-cebe-remove]')) control = 'remove';
    else if (active.matches('[data-cebe-qty-step]')) control = 'step:' + active.dataset.cebeQtyStep;
    else if (active.matches('.cebe-line__title')) control = 'title';
    return { key: line.dataset.cebeKey, index: $$('[data-cebe-key]', scope).indexOf(line), control: control };
  }

  function restoreFocus(scope, descriptor) {
    if (!descriptor) return;
    var active = document.activeElement;
    var target = null;
    if (descriptor.key) {
      var lines = $$('[data-cebe-key]', scope);
      // Same line; if it is gone, the line now at its position, else the one before.
      var line = lineByKey(scope, descriptor.key) || lines[descriptor.index] || lines[descriptor.index - 1] || lines[lines.length - 1];
      if (line) {
        if (descriptor.control === 'remove') target = $('[data-cebe-remove]', line);
        else if (descriptor.control === 'title') target = $('.cebe-line__title', line);
        else if (descriptor.control.indexOf('step:') === 0) target = $('[data-cebe-qty-step="' + descriptor.control.slice(5) + '"]', line);
        else target = $('[data-cebe-qty] input', line);
      }
    } else if (scope.contains(active) && active.getClientRects().length) {
      return; // still there and visible
    }
    // Never left on a hidden or removed control ("Check out" when the foot hides): "Explore coffee", else close.
    target = target || $('.cebe-drawer__empty a', scope) || $('[data-cebe-drawer-close]', scope) || $('[data-cebe-checkout]', scope);
    if (target && active !== target) target.focus();
  }

  // Hairlines at head and foot only while content scrolls under them (like the header's data-scrolled).
  // On short screens the whole panel scrolls, not the list: then "more" is what lies below the panel's own view.
  function updateEdges() {
    if (!cebeShared.drawer) return;
    var body = $('[data-cebe-part="body"]', cebeShared.drawer);
    var top = body ? body.scrollTop : 0;
    var listMore = Boolean(body) && top + body.clientHeight < body.scrollHeight - 1;
    var panelMore = cebeShared.drawer.scrollTop + cebeShared.drawer.clientHeight < cebeShared.drawer.scrollHeight - 1;
    toggleAttr(cebeShared.drawer, 'data-scrolled', top + cebeShared.drawer.scrollTop > 0);
    toggleAttr(cebeShared.drawer, 'data-more', listMore || panelMore);
  }

  // Head, one whole line and the foot (with express buttons and legal lines it grows) must fit at once; if they do
  // not (not only below 560 px, e.g. 320 × 568 with express), the whole panel scrolls like on a short screen
  // (data-tight, assets/cebe.css). The heights do not depend on the mode, so the decision never flips back and forth.
  function updateFit() {
    if (!cebeShared.drawer || !cebeShared.drawer.open) return;
    var head = $('.cebe-drawer__head', cebeShared.drawer);
    var line = $('[data-cebe-key]', cebeShared.drawer);
    var foot = $('[data-cebe-drawer-foot]', cebeShared.drawer);
    var error = $('[data-cebe-drawer-error]', cebeShared.drawer);
    var need = line && foot && !foot.hidden ? head.offsetHeight + line.offsetHeight + foot.offsetHeight + (error && !error.hidden ? error.offsetHeight : 0) : 0;
    toggleAttr(cebeShared.drawer, 'data-tight', need > cebeShared.drawer.clientHeight);
  }

  // Wallet buttons are never re-created; hide the block when the cart is empty or Shopify drew nothing, and while
  // "Check out" is held (syncHeld).
  function updateExpress(count) {
    var express = cebeShared.drawer && $('[data-cebe-express]', cebeShared.drawer);
    if (express) express.hidden = Boolean(express.cebeHeld) || count === 0 || express.childElementCount === 0;
  }

  // False when the answer holds no drawer; the caller then reloads it.
  function renderDrawer(html, opts) {
    if (!cebeShared.drawer) return true;
    var fresh = html && $('[data-cebe-drawer]', parse(html));
    if (!fresh) return false;
    var descriptor = cebeShared.drawer.open ? focusDescriptor(cebeShared.drawer) : null;
    var body = $('[data-cebe-part="body"]', cebeShared.drawer);
    var scrollTop = opts && opts.keepScroll && body ? body.scrollTop : 0;
    var previousImages = $$('.cebe-line__media img', cebeShared.drawer);
    swapParts(cebeShared.drawer, fresh);
    $$('.cebe-line__media img', cebeShared.drawer).forEach(function (img) {
      if (previousImages.indexOf(img) === -1) img.loading = 'eager';
    });
    if (opts && opts.gift) cebeShared.giftAdopt(fresh); // the gift block is never swapped; after the back-forward cache it is read
    var count = updateCount(cebeShared.drawer);
    var foot = $('[data-cebe-drawer-foot]', cebeShared.drawer);
    if (foot) foot.hidden = count === 0;
    updateExpress(count);
    body = $('[data-cebe-part="body"]', cebeShared.drawer);
    if (body && scrollTop) body.scrollTop = scrollTop;
    updateFit();
    updateEdges();
    restoreFocus(cebeShared.drawer, descriptor);
    return true;
  }

  // Reload from the server (after a rejected change or a missing section). True when drawn or a newer answer rules.
  // gift = true: also take the gift option from the answer (back-forward cache only).
  function refreshDrawer(gift) {
    var ticket = ++cebeShared.issued;
    return limitedJson(config.routes.root + '?sections=' + SECTION, { headers: { Accept: 'application/json' } }, CART_WAIT)
      .then(function (data) {
        return !cebeShared.current(ticket) || renderDrawer(data[SECTION], { keepScroll: true, gift: gift === true });
      })
      .catch(function () {
        return false;
      });
  }

  function onCartPage() {
    return document.body.classList.contains('cebe-template-cart');
  }

  function drawerAvailable() {
    return Boolean(cebeShared.drawer && typeof cebeShared.drawer.showModal === 'function' && window.fetch && !onCartPage());
  }

  function openDrawer(trigger) {
    if (!drawerAvailable()) return false;
    $$('.cebe-line__media img', cebeShared.drawer).forEach(function (img) {
      img.loading = 'eager';
    });
    // The phone buy bar is aria-hidden; return focus to the real button instead.
    if (trigger && trigger.closest && trigger.closest('[data-cebe-buybar]')) {
      trigger = $('[data-cebe-product-form] [data-cebe-add]') || trigger;
    }
    cebeShared.lastTrigger = trigger || document.activeElement;
    if (config.closeMenu) config.closeMenu();
    window.clearTimeout(cebeShared.closeTimer);
    cebeShared.drawer.removeAttribute('data-closing');
    if (!cebeShared.drawer.open) {
      // The page behind stands still; on systems with a classic scrollbar the header keeps its exact width.
      var root = document.documentElement;
      root.style.setProperty('--cebe-scrollbar-comp', Math.max(0, window.innerWidth - root.clientWidth) + 'px');
      root.classList.add('cebe-drawer-open');
      cebeShared.drawer.showModal(); // the close button has autofocus
      void cebeShared.drawer.offsetWidth; // style flush at the right edge, so data-shown starts the slide
    }
    cebeShared.drawer.setAttribute('data-shown', ''); // from wherever the panel is, also mid-close
    if (!cebeShared.drawer.contains(document.activeElement)) {
      var close = $('[data-cebe-drawer-close]', cebeShared.drawer);
      if (close) close.focus();
    }
    updateFit();
    updateEdges();
    return true;
  }

  function finishClose() {
    window.clearTimeout(cebeShared.closeTimer);
    if (cebeShared.drawer.open) cebeShared.drawer.close();
  }

  // Slides back to the right edge (220 ms), then closes. Reduced motion: closes at once.
  function closeDrawer() {
    if (!cebeShared.drawer || !cebeShared.drawer.open || cebeShared.drawer.hasAttribute('data-closing')) return;
    if (!motionOk()) return cebeShared.drawer.close();
    cebeShared.drawer.setAttribute('data-closing', '');
    cebeShared.drawer.removeAttribute('data-shown');
    cebeShared.closeTimer = window.setTimeout(finishClose, 300); // safety net if transitionend never comes
  }
  /* ---- end of lines 8–389 ---- */

  cebeShared.config = config;
  cebeShared.reduceMotion = reduceMotion;
  cebeShared.$ = $;
  cebeShared.$$ = $$;
  cebeShared.announce = announce;
  cebeShared.readJson = readJson;
  cebeShared.errorText = errorText;
  cebeShared.CART_WAIT = CART_WAIT;
  cebeShared.limitedJson = limitedJson;
  cebeShared.initHeader = initHeader;
  cebeShared.initMenu = initMenu;
  cebeShared.SECTION = SECTION;
  cebeShared.parse = parse;
  cebeShared.fill = fill;
  cebeShared.currentCount = currentCount;
  cebeShared.showError = showError;
  cebeShared.lineByKey = lineByKey;
  cebeShared.swapParts = swapParts;
  cebeShared.focusDescriptor = focusDescriptor;
  cebeShared.restoreFocus = restoreFocus;
  cebeShared.updateEdges = updateEdges;
  cebeShared.updateFit = updateFit;
  cebeShared.updateExpress = updateExpress;
  cebeShared.renderDrawer = renderDrawer;
  cebeShared.refreshDrawer = refreshDrawer;
  cebeShared.onCartPage = onCartPage;
  cebeShared.drawerAvailable = drawerAvailable;
  cebeShared.openDrawer = openDrawer;
  cebeShared.finishClose = finishClose;
  cebeShared.closeDrawer = closeDrawer;
  cebeShared.done = 1;
})();

